import type {ReactElement} from 'react';
import {useRef} from 'react';
import {useEffect} from 'react';
import {useState} from 'react';
import React from 'react';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {getPosterWidthAndHeight, getUnitsFromPosterTypeObject, isCustomSize, PosterTypeUnits} from '@Libraries/poster-object.library';
import {noop} from '@Utils/general.util';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {BaseSizeDropdownSelector} from '@Components/download-panel/components/base-size-dropdown-selector/base-size-dropdown-selector';
import {BaseSizeDropdown} from '@Components/download-panel/components/base-size-dropdown/base-size-dropdown';
import {SizeDropdownOptions} from '@Components/download-panel/components/download-design-size-option-wrapper/download-design-size-option-wrapper.types';
import {ImageDesignSizePopup} from '@Components/download-panel/components/image-design-size-popup/image-design-size-popup';
import {useUpdateMoreOptionsModal} from '@Components/modal/hooks/useUpdateMoreOptionsModal';
import {ControlledSlider} from '@Components/controlled-slider';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';

interface DownloadImageDesignSizeOptionProps {
  onSlideCallback?(): void;
  onSlideEndedCallback?(): void;
}

export function DownloadImageDesignSizeOption({onSlideCallback = noop, onSlideEndedCallback = noop}: DownloadImageDesignSizeOptionProps): ReactElement {
  const {scale, setScale, defaultOptions, posterObject, selectedDisplaySizeOption, setSelectedDisplaySizeOption} = useDownloadPanelData();
  const initialRender = useRef(true);
  const [min, setMin] = useState(0.1);
  const [max, setMax] = useState(4);
  const isSliderBeingDragged = useAppSelector((state) => {
    return state.downloadDialog.isSliderBeingDragged;
  });
  const dispatch = useAppDispatch();

  const getSliderOption = (): ReactElement => {
    return (
      <div className="spacing-m-t-2">
        <ControlledSlider marks={[1]} step={0.1} min={min} max={max} value={scale ?? 1} onSlide={onSlide} onSlideEnded={onSlideEnded} />
      </div>
    );
  };

  const onClickListItem = (id: SizeDropdownOptions, isClickFromModal = false): void => {
    setSelectedDisplaySizeOption(id);
    switch (id) {
      case SizeDropdownOptions.ORIGINAL:
        setScale(1);
        break;
      case SizeDropdownOptions.LARGE: {
        const {width} = getLargeWidthAndHeight();
        const scaleFactorLarge = width / getPosterWidthAndHeight(posterObject).width;
        setScale(scaleFactorLarge);
        break;
      }
      case SizeDropdownOptions.SMALL: {
        const {width: smallWidth} = getSmallWidthAndHeight();
        const scaleFactorSmall = smallWidth / getPosterWidthAndHeight(posterObject).width;
        setScale(scaleFactorSmall);
        break;
      }
      case SizeDropdownOptions.CUSTOM:
      default:
        return;
    }

    if (isClickFromModal) {
      dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
    }
  };

  const modalContent = (
    <ImageDesignSizePopup
      getSliderOption={getSliderOption}
      onClickListItem={(id): void => {
        onClickListItem(id, true);
      }}
      scale={scale ?? 1}
      posterObject={posterObject}
      selectedDisplaySizeOption={selectedDisplaySizeOption}
      setSelectedDisplaySizeOption={setSelectedDisplaySizeOption}
    />
  );
  useUpdateMoreOptionsModal(modalContent, [scale, selectedDisplaySizeOption, posterObject]);

  const onSlideEnded = (): void => {
    onSlideEndedCallback();
  };
  const onSlide = (value: number): void => {
    const dp = 1;
    const roundedValue = Number(value.toFixed(dp));
    setScale(roundedValue);
    onSlideCallback();
  };

  const getMinMaxFromUnits = (units: PosterTypeUnits): {min: number; max: number} => {
    switch (units) {
      case PosterTypeUnits.MM:
        return {
          min: 20,
          max: 2400,
        };
      case PosterTypeUnits.CM:
        return {
          min: 2,
          max: 240,
        };
      case PosterTypeUnits.FT:
        return {
          min: 0.1,
          max: 8,
        };
      case PosterTypeUnits.INCH:
        return {
          min: 1,
          max: 96,
        };
      case PosterTypeUnits.PX:
      default:
        return {
          min: 50,
          max: 8000,
        };
    }
  };
  const getMinMaxForImagePosters = (): {min: number; max: number} => {
    const data = getMinMaxFromUnits(getUnitsFromPosterTypeObject(posterObject));

    if (isCustomSize(posterObject.type.name)) {
      return {
        min: data.min / Math.min(posterObject.userWidth, posterObject.userHeight),
        max: data.max / Math.max(posterObject.userWidth, posterObject.userHeight),
      };
    }

    const {width, height} = getPosterWidthAndHeight(posterObject);

    return {
      min: data.min / Math.min(width, height),
      max: data.max / Math.max(width, height),
    };
  };

  const setInitialScale = (value: number): void => {
    if (defaultOptions?.scale && initialRender.current) {
      return;
    }

    initialRender.current = false;
    setScale(value);
  };

  useEffect(() => {
    const {min, max} = getMinMaxForImagePosters();
    setMin(Math.max(min, 0.1));
    setMax(max);
    setInitialScale(1);
  }, [posterObject.type]);

  const isCustomOptionSelected = (): boolean => {
    return selectedDisplaySizeOption === SizeDropdownOptions.CUSTOM;
  };

  const getSmallWidthAndHeight = (): {width: number; height: number} => {
    const {width, height} = getPosterWidthAndHeight(posterObject);
    const minDimension = Math.min(width, height);
    let threshold: number;

    switch (getUnitsFromPosterTypeObject(posterObject)) {
      case PosterTypeUnits.CM:
        threshold = 15;
        break;
      case PosterTypeUnits.FT:
        threshold = 0.5;
        break;
      case PosterTypeUnits.MM:
        threshold = 150;
        break;
      case PosterTypeUnits.INCH:
        threshold = 6;
        break;
      case PosterTypeUnits.PX:
      default:
        threshold = 540;
    }

    const scaleFactor = threshold / minDimension;
    return {
      width: Number((width * scaleFactor).toFixed(1)),
      height: Number((height * scaleFactor).toFixed(1)),
    };
  };

  const getLargeWidthAndHeight = (): {width: number; height: number} => {
    const {width, height} = getPosterWidthAndHeight(posterObject);
    const maxDimension = Math.max(width, height);
    let threshold: number;

    switch (getUnitsFromPosterTypeObject(posterObject)) {
      case PosterTypeUnits.CM:
        threshold = 90;
        break;
      case PosterTypeUnits.FT:
        threshold = 4;
        break;
      case PosterTypeUnits.MM:
        threshold = 900;
        break;
      case PosterTypeUnits.INCH:
        threshold = 36;
        break;
      case PosterTypeUnits.PX:
      default:
        threshold = 6000;
    }

    const scaleFactor = threshold / maxDimension;
    return {
      width: Number((width * scaleFactor).toFixed(1)),
      height: Number((height * scaleFactor).toFixed(1)),
    };
  };

  const getTextForEachOption = (id: SizeDropdownOptions, forcefullyShowDimensionsWithCustom = false): string => {
    switch (id) {
      case SizeDropdownOptions.ORIGINAL:
        return `${window.i18next.t('pmwjs_original')} · ${getPosterWidthAndHeight(posterObject).width} x ${getPosterWidthAndHeight(posterObject).height}${getUnitsFromPosterTypeObject(posterObject)}`;
      case SizeDropdownOptions.SMALL:
        return `${window.i18next.t('pmwjs_small')} · ${getSmallWidthAndHeight().width} x ${getSmallWidthAndHeight().height}${getUnitsFromPosterTypeObject(posterObject)}`;
      case SizeDropdownOptions.LARGE:
        return `${window.i18next.t('pmwjs_large')} · ${getLargeWidthAndHeight().width} x ${getLargeWidthAndHeight().height}${getUnitsFromPosterTypeObject(posterObject)}`;
      case SizeDropdownOptions.CUSTOM:
        return isCustomOptionSelected() || forcefullyShowDimensionsWithCustom
          ? ` ${window.i18next.t('pmwjs_custom')} · ${parseFloat((getPosterWidthAndHeight(posterObject).width * scale).toFixed(2))} x ${parseFloat((getPosterWidthAndHeight(posterObject).height * scale).toFixed(2))}${getUnitsFromPosterTypeObject(posterObject)}`
          : window.i18next.t('pmwjs_custom');
      default:
        return '';
    }
  };

  const getSelectorTextForDropdown = (): string => {
    return getTextForEachOption(selectedDisplaySizeOption, true);
  };

  const getSelectorForDropdown = (): ReactElement => {
    return (
      <div>
        <BaseSizeDropdownSelector text={getSelectorTextForDropdown()} />
      </div>
    );
  };

  const getPopupForDropdown = (): ReactElement => {
    return (
      <ImageDesignSizePopup
        getSliderOption={getSliderOption}
        onClickListItem={onClickListItem}
        scale={scale ?? 1}
        posterObject={posterObject}
        selectedDisplaySizeOption={selectedDisplaySizeOption}
        setSelectedDisplaySizeOption={setSelectedDisplaySizeOption}
      />
    );
  };

  return (
    <BaseSizeDropdown
      modalTitle={window.i18next.t('pmwjs_size')}
      popup={getPopupForDropdown()}
      modalContent={modalContent}
      selector={getSelectorForDropdown()}
      doesCloseOnOuterClick={!isSliderBeingDragged}
    />
  );
}
